import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { trackPageView } from "./analyticsService";
import * as Sentry from "@sentry/react";
import { uxCamTagScreen } from "../../utils/UXCam";

/**
 * The last pathname reported to Mixpanel / Sentry / UXCam. Lives at module
 * level because it must survive PageViewAnalytics remounting: Root swaps the
 * auth and main router stacks on login/logout, and the freshly mounted
 * instance would otherwise re-report the screen the old instance already
 * tracked. It is also shared with fixturesSegments, whose segment switches
 * bypass the router via history.replaceState.
 */
let lastTrackedPathname: string | undefined;

/**
 * Report a page view (Mixpanel event, Sentry breadcrumb, UXCam screen tag)
 * unless `pathname` is the one most recently reported. Real revisits always
 * pass through another pathname in between, so consecutive repeats can only
 * be double-reports of the same visit.
 */
export function trackPageViewOnce(pathname: string) {
    if (pathname === lastTrackedPathname) {
        return;
    }
    lastTrackedPathname = pathname;

    trackPageView(pathname);
    Sentry.addBreadcrumb({
        category: "pageView",
        message: pathname,
    });

    // uxCamTagScreen no-ops when the native UXCam plugin isn't present.
    uxCamTagScreen(pathname);
}

export function resetPageViewTrackingForTests() {
    lastTrackedPathname = undefined;
}

/**
 * This component is just a black hole into which all route changes fall.
 *
 * SCORZA-703: the tracking runs in an effect keyed on the pathname, not in the
 * render body — a render is not a page visit. SCORZA-726: the report is
 * deferred by one tick and cancelled if the pathname changes first, so
 * synchronous redirect chains (/ bouncing to the landing screen, login
 * bouncing through onboarding) only report the screen the user ends up on.
 */
export function PageViewAnalytics() {
    const { pathname } = useLocation();

    useEffect(() => {
        const timer = setTimeout(() => trackPageViewOnce(pathname));
        return () => clearTimeout(timer);
    }, [pathname]);

    return null;
}
